/**
 * TalknWidgets Configuration Interface
 *
 * Advanced configuration options for explicit initialization
 */

export interface TalknWidgetsConfig {
  /**
   * Contract API key used for authenticated Socket.IO connections.
   */
  apiKey?: string;

  /**
   * Optional Socket.IO host override. Example: "localhost:10444"
   */
  host?: string;

  /**
   * Optional Socket.IO protocol override. Example: "https:" or "http:"
   */
  protocol?: string;

  /**
   * Allowed origins for CORS (default: [window.location.origin])
   */
  origins?: string[];

  /**
   * Custom WebWorker URL
   */
  workerUrl?: string;

  /**
   * Worker loading mode
   * - 'inline': Use blob: URLs (requires CSP worker-src blob:)
   * - 'static': Use static worker files (CSP-safe)
   * - 'auto': Auto-detect based on CSP restrictions (default)
   */
  workerMode?: 'inline' | 'static' | 'auto';

  /**
   * Subresource Integrity (SRI) hashes for verification
   */
  sriHashes?: Record<string, string>;

  /**
   * CSP nonce for inline scripts
   */
  cspNonce?: string;

  /**
   * Custom monitoring endpoint
   */
  monitoringEndpoint?: string;

  /**
   * Authentication token for monitoring endpoint
   */
  monitoringAuth?: string;

  /**
   * Signing key for monitoring payload integrity
   */
  monitoringSignKey?: string;

  /**
   * Privacy configuration for data protection
   */
  privacy?: {
    excludeFields?: string[];
    hashUserIds?: boolean;
    ipAnonymization?: boolean;
  };

  /**
   * Auto-render after initialization (default: true)
   */
  autoRender?: boolean;
}

export class TalknWidgetsConfigManager {
  private static config: TalknWidgetsConfig = {
    workerMode: 'auto',
    autoRender: true
  };

  private static initialized = false;

  static setConfig(userConfig: TalknWidgetsConfig): void {
    this.config = { ...this.config, ...userConfig };

    // Set default origins if not provided (same-origin)
    if (!this.config.origins && typeof window !== 'undefined') {
      this.config.origins = [window.location.origin];
    }
  }

  static getConfig(): TalknWidgetsConfig {
    return { ...this.config };
  }

  static isInitialized(): boolean {
    return this.initialized;
  }

  static markInitialized(): void {
    this.initialized = true;
  }

  /**
   * Verify current origin against allowed origins
   */
  static verifyOrigin(): boolean {
    if (typeof window === 'undefined') {
      return true; // SSR environment
    }

    const origins = this.config.origins;
    if (!origins || origins.length === 0) {
      return true; // No restrictions
    }

    const currentOrigin = window.location.origin;
    return origins.includes(currentOrigin);
  }

  /**
   * Detect worker loading mode based on CSP
   */
  static async detectWorkerMode(): Promise<'inline' | 'static'> {
    const configuredMode = this.config.workerMode;

    if (configuredMode === 'inline' || configuredMode === 'static') {
      return configuredMode;
    }

    // Auto-detect CSP restrictions
    try {
      // Test if blob: URLs are allowed
      const testBlob = new Blob([''], { type: 'application/javascript' });
      const testUrl = URL.createObjectURL(testBlob);

      // Try to create worker with blob URL
      try {
        const testWorker = new Worker(testUrl);
        testWorker.terminate();
        URL.revokeObjectURL(testUrl);
        return 'inline'; // blob: URLs work
      } catch (e) {
        URL.revokeObjectURL(testUrl);
        return 'static'; // blob: URLs blocked by CSP
      }
    } catch (e) {
      // Blob creation failed - use static worker
      return 'static';
    }
  }
}
